import { max } from 'lodash-es';

// The placement arithmetic of cs-card-list, kept apart from the DOM so it can be unit-tested.
// The component measures the cards and the container, calls computeCardListLayout, and then
// only writes the results onto the page.

/** One card as the list measured it: its natural width and its cached height. */
export interface CardListCardSize {
  width: number;
  height: number;
}

export interface CardListLayoutInput {
  cards: CardListCardSize[];
  containerWidth: number;
  // only read when verticallyCentered is set
  containerHeight: number;
  paddingLeft: number;
  paddingRight: number;
  gap: number;
  grid: boolean;
  verticallyCentered: boolean;
}

export interface CardListLayout {
  // the width every card is given (the widest card, clamped to the room inside the padding)
  cardWidth: number;
  // one entry per input card, in the same order: where its top-left corner goes
  positions: { x: number, y: number }[];
  // the height the container must take in px, or null when it fills its parent (vertically centred)
  containerHeight: number | null;
}

/** Work out where each card of a cs-card-list goes. `cards` must not be empty. */
export function computeCardListLayout(input: CardListLayoutInput): CardListLayout {
  const { cards, gap } = input;

  let currOffsetY = 0;
  // if the list is vertically centred, start low enough that the stack sits in the middle
  if (input.verticallyCentered) {
    const totalHeight = cards.reduce((sum, c) => sum + c.height, 0);
    currOffsetY = Math.max((input.containerHeight - totalHeight) / 2, 0);
  }

  const availableWidth = input.containerWidth - input.paddingLeft - input.paddingRight;
  const cardWidth = Math.min(max(cards.map(c => c.width)), availableWidth);
  let columns = Math.floor((availableWidth + gap) / (cardWidth + gap));
  if (columns < 1 || !input.grid) {
    columns = 1;
  }
  const totalColumnsWidth = columns * cardWidth + (columns - 1) * gap;
  const leftColumnX = input.paddingLeft + (availableWidth - totalColumnsWidth) / 2;

  const positions: { x: number, y: number }[] = [];
  let currColumn = 0;
  let currRowHeight = 0;
  for (let i = 0; i < cards.length; i++) {
    positions.push({ x: leftColumnX + currColumn * (cardWidth + gap), y: currOffsetY });

    currRowHeight = Math.max(currRowHeight, cards[i].height);
    currColumn++;
    // if the row is full, move down to the next one (with a gap, unless this was the last card)
    if (currColumn >= columns) {
      currOffsetY += currRowHeight;
      if (i < cards.length - 1) {
        currOffsetY += gap;
      }
      currColumn = 0;
      currRowHeight = 0;
    }
  }
  // if the last row is only part full, the loop never closed it, so add its height now
  // (no gap: it is always the last row)
  if (currColumn > 0) {
    currOffsetY += currRowHeight;
  }

  // the bottom padding below the last card is one gap
  const containerHeight = input.verticallyCentered ? null : currOffsetY + gap;
  return { cardWidth, positions, containerHeight };
}
